import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  Divider,
  LargeFormFieldPresentational
} from '@cmhc-schl/hearth-ui';

import styles from './styles.module.scss';
import { Loan, LoanUpdateProps } from './types';
import { useTranslation } from 'react-i18next';
import LoanStatusPill from '../LoanStatusPill';
import { loanStatusMapping as portfolioManagementStatusMapping } from '../../../HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/utils/LoanStatusMapping';
import { loanStatusMapping } from '../../components/HbtMasterPoolTable/utils/LoanStatusMapping';
import PortfolioLoanStatusPill from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/LoanStatusPill';
import { getLoanStatusLabel } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/columns/loansInventoryTableCellRenderers';
import { onAddLoans } from '../../components/HbtMasterPool/component/ForceStatusFlyout/Utils/AddLoans';
import { useAuthenticationContext } from 'Foundation/Authentication';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import { PoolProcessingStatusCode } from '../../components/HbtMasterPool/component/ForceStatusFlyout/types';

const LoansToBeUpdated: React.FC<LoanUpdateProps> = ({
  flyoutLoans,
  onNotify,
  onErrorLoans,
  proceedWithEligibleLoans,
  setFlyoutLoans,
  setShowAddSection,
  showAddSection,
  approvalStatus,
  statusRequiredError,
  setStatusRequiredError,
  setIsAddingLoans,
  isAddingLoans,
  newLoansInput,
  setNewLoansInput,
  addLoanslargeFormFieldErrMsg,
  setAddLoansLargeFormFieldErrMsg,
  loansTypeToBeAdded,
  textFieldPlaceholder,
  textFieldButtonText,
  addLoansDescription,
  addLoansTextFieldError,
  toasterHeader,
  toasterDesc,
  addLoansTextFieldErrorTwo,
  typeOfFlyout,
  typeOfPortfolioInsurance,
  pptCode,
  applicationID,
  loansData,
  isFromLoansInventory,
  masterPoolCmhcAccountNumber,
  requestUrl,
  setEligibleLoans,
  loansUpdateLargeFormFieldButtonText
}) => {
  const { t } = useTranslation('HbtMasterPool');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<String[]>([]);
  const largeFormFieldMaxChar = 300;
  const loanMapping = loanStatusMapping(t);
  const authenticationContext = useAuthenticationContext();
  const differentStatusErrorCode = 'HBT_ERR_12012';
  const cannotUpdateReviewRequiredRemarkErrorCode = 'HBT_ERR_12013';
  const duplicateLoans = 'HBT_ERR_3003';

  useEffect(() => {
    if (flyoutLoans.length === 0) {
      setShowAddSection(true);
    }
  }, [flyoutLoans]);

  useEffect(() => {
    if (proceedWithEligibleLoans && flyoutLoans.length > 0) {
      setShowAddSection(false);
    }
  }, [proceedWithEligibleLoans]);

  const handleToggleAddSection = () => {
    setShowAddSection(!showAddSection);
    if (!showAddSection) {
      setNewLoansInput('');
      setErrors([]);
    }
  };

  const handleAddLoansInput = (value: string) => {
    setNewLoansInput(value);
    setAddLoansLargeFormFieldErrMsg('');
  };

  const handleAddLoans = async () => {
    if (typeOfFlyout !== 'updateRemark' && !approvalStatus) {
      setStatusRequiredError(true);
      return;
    }
    setStatusRequiredError(false);

    if (!newLoansInput.trim()) {
      setAddLoansLargeFormFieldErrMsg(
        (addLoansTextFieldError ?? pptCode === PoolProcessingStatusCode.NON_CANCELLED)
          ? t('Add-Loans-Empty-Error-Msg-Non-Cancelled')
          : t('Add-Loans-Empty-Error-Msg-Cancelled')
      );
      return;
    }

    const loanNumbers = newLoansInput
      .split('\n')
      .map((num) => num.trim())
      .filter((num) => num.length > 0);

    if (loanNumbers.length === 0) return;

    setIsSubmitting(true);
    setIsAddingLoans(true);
    setErrors([]);

    try {
      let result = await onAddLoans({
        loans: loanNumbers,
        approvalStatus,
        flyoutLoans,
        authenticationContext,
        typeOfPortfolioInsurance,
        pptCode,
        applicationID,
        typeOfFlyout,
        loansData,
        isFromLoansInventory,
        masterPoolCmhcAccountNumber,
        requestUrl
      });

      if (result.success) {
        setAddLoansLargeFormFieldErrMsg('');
        const eligible = result.loans
          .filter((loan: Loan) => !loan.hasError)
          .map((loan: Loan) => ({ ...loan, isNew: true }));
        const rejected = result.loans.filter((loan: Loan) => loan.hasError);

        if (rejected.length) {
          setEligibleLoans(eligible);
          onErrorLoans(rejected, eligible);
          return;
        }

        if (eligible.length) {
          setFlyoutLoans([...eligible]);
          onNotify(
            'icon_check_circle',
            toasterHeader ?? t('Loan-Add-Success-Toast'),
            toasterDesc ?? t('Loan-Add-Success-Toast-Desc')
          );
        }

        setNewLoansInput('');
        setShowAddSection(false);
      } else {
        if (result.errorCode && result.errorCode[0] === differentStatusErrorCode) {
          pptCode === PoolProcessingStatusCode.CANCELLED
            ? setAddLoansLargeFormFieldErrMsg(t('Loans-Have-Different-Status-CMHC-Account-Number'))
            : setAddLoansLargeFormFieldErrMsg(
                t('Loans-Have-Different-Status-Lenderreference-Number')
              );
          return;
        }
        if (result.errorCode && result.errorCode[0] === cannotUpdateReviewRequiredRemarkErrorCode) {
          pptCode === PoolProcessingStatusCode.CANCELLED
            ? setAddLoansLargeFormFieldErrMsg(t('Cannot-Update-Review-Required'))
            : setAddLoansLargeFormFieldErrMsg(t('Cannot-Update-Review-Required-New-Portfolio'));
          return;
        }
        if (result.errorCode && result.errorCode[0] === duplicateLoans) {
          pptCode === PoolProcessingStatusCode.CANCELLED
            ? setAddLoansLargeFormFieldErrMsg(t('Duplicate-Loans'))
            : setAddLoansLargeFormFieldErrMsg(t('Duplicate-Loans-New-Portfolio'));
          return;
        }
        setAddLoansLargeFormFieldErrMsg(result.error[0]);
      }
    } catch (error) {
      setErrors([t('Loan-Add-Error')]);
      onNotify('icon_error_outlined', 'error', 'add loans failed');
    } finally {
      setIsSubmitting(false);
      setIsAddingLoans(false);
    }
  };

  const handleDeleteLoan = (loanId: string) => {
    if (flyoutLoans.length <= 1) {
      setFlyoutLoans([]);
      setShowAddSection(true);
    } else {
      setFlyoutLoans((prevLoans) => prevLoans?.filter((loan) => loan.id !== loanId));
    }
    onNotify('icon_delete', ' ', t('Loan-Delete-Success-Toast'));
  };

  const getRowClassName = (loan: Loan) => {
    if (loan.hasError) return `${styles.loansTableRow} ${styles.loansTableRowError}`;
    if (loan.isNew) {
      return `${styles.loansTableRow} ${styles.loansTableRowNew}`;
    }
    return styles.loansTableRow;
  };

  return (
    <Box className={styles.loansToBeUpdated}>
      {/* Loans Table */}
      {flyoutLoans.length > 0 && (
        <TableContainer component={Paper} className={styles.loansTableContainer} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow className={styles.loansTableHeader}>
                <TableCell className={styles.loansTableHeaderCell}>
                  {loansTypeToBeAdded ?? t('CMHC-Account-Number')}
                </TableCell>
                {typeOfFlyout === 'updateRemark' ? (
                  <TableCell className={styles.loansTableHeaderCell}>
                    <Stack direction="row" gap={1}>
                      {t('Add-Remarks-Desc')}
                      <Tooltip
                        title={t('Remarks-Table-Header-Tooltip')}
                        placement="top-start"
                        arrow
                        slotProps={{
                          tooltip: {
                            className: styles.requestOverviewDetailsToolTip
                          },
                          arrow: {
                            className: styles.tooltipArrow
                          }
                        }}
                      >
                        <div>
                          <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
                        </div>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                ) : (
                  <TableCell className={styles.loansTableHeaderCell}>{t('Status')}</TableCell>
                )}
                <TableCell
                  className={`${styles.loansTableHeaderCell} ${styles.loansTableHeaderCellActions}`}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {flyoutLoans.map((loan) => (
                <TableRow key={loan.id} className={getRowClassName(loan)}>
                  <TableCell className={styles.loansTableCell}>
                    <Box className={styles.loansAccountNumber}>
                      {loan.hasError && (
                        <CmhcIcon size={CmhcIconSize.SMALL} type="icon_error_outlined" />
                      )}
                      {loan.validationReviewLoanData?.accountNumber ?? loan.accountNumber ?? ''}
                    </Box>
                  </TableCell>
                  {typeOfFlyout === 'updateRemark' ? (
                    <TableCell className={styles.loansTableCell}>
                      <MemoizedHbtDataTableCell
                        dataValue={String(loan.lastRemark)}
                        cellContent={
                          <Box title={loan.lastRemark} className={styles.loansRemark}>
                            {loan.lastRemark}
                          </Box>
                        }
                      />
                    </TableCell>
                  ) : (
                    <TableCell className={`${styles.loansTableCell} ${styles.loanStatusPill}`}>
                      {typeOfPortfolioInsurance === 'validationReview' ? (
                        <>
                          <PortfolioLoanStatusPill
                            status={loan.status}
                            label={getLoanStatusLabel(
                              loan.status as keyof typeof portfolioManagementStatusMapping,
                              t
                            )}
                            filled={false}
                          />
                        </>
                      ) : (
                        <>
                          <LoanStatusPill
                            status={loan.status}
                            label={loanMapping[loan.status as keyof typeof loanMapping]}
                            filled={false}
                          />
                        </>
                      )}
                    </TableCell>
                  )}
                  <TableCell className={styles.loansTableCell}>
                    <IconButton
                      className={styles.loansDeleteBtn}
                      size="small"
                      onClick={() => handleDeleteLoan(loan.id)}
                    >
                      <CmhcIcon size={CmhcIconSize.SMALL} type="icon_close" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {flyoutLoans.length > 0 && (
        <>
          <Box className={styles.addLoansButtonWrapper}>
            <Button
              customStyles={{ textDecoration: 'none' }}
              type="button"
              size={ButtonSize.SMALL}
              name={`btn`}
              loading={isAddingLoans}
              leadingIcon={() => (
                <CmhcIcon size={CmhcIconSize.SMALL} type="icon_add_circle_outlined" />
              )}
              onClick={handleToggleAddSection}
              variant={ButtonVariant.SECONDARY}
              text={{
                value: textFieldButtonText ?? t('Add-Loans')
              }}
              ariaText={{
                value: textFieldButtonText ?? t('Add-Loans')
              }}
            />
            <Box>
              {t(flyoutLoans.length === 1 ? 'ItemsSingular' : 'ItemsPlural', {
                count: flyoutLoans.length
              })}
            </Box>
          </Box>
        </>
      )}

      <Collapse in={showAddSection}>
        <Box
          data-testid="loans-update-add-section"
          className={`${styles.loansAddSection} ${
            flyoutLoans.length > 0 ? styles.addTopShadow : ''
          }`}
        >
          <Typography className={styles.loansAddDescription}>
            {addLoansDescription ?? t('Add-Loans-Breif-Text')}
          </Typography>

          <Typography className={styles.loansInputLabel}>
            {loansTypeToBeAdded ?? t('CMHC-Account-Numbers-Text')}
          </Typography>

          <LargeFormFieldPresentational
            ariaText="form"
            id="large-form-field"
            placeholder={textFieldPlaceholder ?? t('Loan-Add-Textbox-Placeholder')}
            value={newLoansInput}
            invalid={!!addLoanslargeFormFieldErrMsg}
            error={addLoanslargeFormFieldErrMsg}
            onChange={(e) => handleAddLoansInput(e.target.value)}
          />

          <Box className={styles.loansAddActions}>
            {flyoutLoans.length > 0 && (
              <Button
                customStyles={{ textDecoration: 'none' }}
                type="button"
                name={`btn`}
                onClick={handleToggleAddSection}
                disabled={isSubmitting}
                variant={ButtonVariant.TERTIARY}
                className={styles.loansCancelBtn}
                text={{
                  value: t('Cancel')
                }}
                ariaText={{
                  value: t('Cancel')
                }}
              />
            )}
            <Button
              type="button"
              name={`btn`}
              onClick={handleAddLoans}
              loading={isAddingLoans}
              variant={ButtonVariant.SECONDARY}
              size={ButtonSize.SMALL}
              className={styles.loansAddBtn}
              disabled={isSubmitting}
              text={{
                value: loansUpdateLargeFormFieldButtonText ?? t('Add-Loans')
              }}
              ariaText={{
                value: loansUpdateLargeFormFieldButtonText ?? t('Add-Loans')
              }}
            />
          </Box>
        </Box>
        <Box>
          {typeOfFlyout !== 'updateRemark' && statusRequiredError && (
            <Typography variant="body2" className={styles.loansStatusError}>
              {addLoansTextFieldErrorTwo ?? t('Force-Status-Add-Loans-Missing-Status-Error-Msg')}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default LoansToBeUpdated;
